import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { ConverterService, EventService, PRODUCT_NORMALIZER } from '@spartacus/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { switchMap, take, takeUntil, map } from 'rxjs/operators';
import { ActiveCartService, MultiCartService } from '@spartacus/cart/base/core';
import { CartAddEntryFailEvent, CartAddEntrySuccessEvent } from '@spartacus/cart/base/root';
import { TranslatePipe } from '@ngx-translate/core';

import {
  BrandsEnum,
  IProductCustomer,
  IWarranty,
  SdButtonComponent,
  SdModalDefaultComponent,
  SdProductCustomerComponent
} from '@seb/web-design-system/core';
import { CustomBaseStore } from '../../../../models';
import { StockStatusEnum } from '../../../../enums';
import { BundleMapper, ProductMapper } from '../../../../mappers';
import {
  BaseStoreService, CartFacadeService,
  ExtendedWarrantyService,
  LoaderService,
  TrackingService
} from '../../../../services';
import {
  ExtendedWarrantyOffersComponent
} from '../../../../components/static/extended-warranty-offers/extended-warranty-offers.component';
import {
  OmnibusRecommendedPriceModalComponent
} from '../../../../components/static/omnibus-recommended-price-modal/omnibus-recommended-price-modal.component';

@Component({
  selector: 'wc-added-to-cart-modal',
  templateUrl: './added-to-cart-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdModalDefaultComponent,
    SdProductCustomerComponent,
    ExtendedWarrantyOffersComponent,
    SdButtonComponent,
    OmnibusRecommendedPriceModalComponent,
    TranslatePipe
  ]
})
export class AddedToCartModalComponent implements OnInit, OnDestroy {
  isOpened = false;
  product: IProductCustomer;
  globalError = false;
  quantityExceededError = false;
  addedWithQuantityExceededError = false;
  addedWithInvalidProductType = false;
  hideContinueShoppingBtn = false;
  invalidProductTypeKey = '';
  isOpenedRecommendedModal = false;
  isBundle: boolean;
  isExtendedWarrantyEnabled = false;
  extendedWarrantyData: IWarranty[] = [];
  private baseStore: CustomBaseStore;
  protected readonly destroy$: Subject<void> = new Subject();

  @Input()
  brand: BrandsEnum;

  constructor(
    private productMapper: ProductMapper,
    private eventService: EventService,
    private converter: ConverterService,
    private bundleMapper: BundleMapper,
    private cartService: CartFacadeService,
    private router: Router,
    private activeCartService: ActiveCartService,
    private multiCartService: MultiCartService,
    private trackingService: TrackingService,
    private loaderService: LoaderService,
    private cdRef: ChangeDetectorRef,
    private baseStoreService: BaseStoreService,
    private extendedWarrantyService: ExtendedWarrantyService,
  ) {
    this.baseStore = this.baseStoreService.baseStore;

    this.isExtendedWarrantyEnabled = this.baseStore?.extendedWarranty?.enabled;

    if (this.isExtendedWarrantyEnabled) {
      this.extendedWarrantyService.getActiveCategories(this.baseStore.extendedWarranty);
    }
  }

  ngOnInit(): void {
    this.eventService
      .get(CartAddEntryFailEvent)
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (event.error?.details?.length > 0 && event.error?.details[0]?.type === 'NotEmptyCartError') {
          this.cartService.openCartCompatibility(event.productCode, event.quantity);
        } else if (event.error?.details?.length > 0 && event.error?.details[0]?.type === 'UniqueItemInCartError') {
          this.cartService.openCartCompatibility(event.productCode, event.quantity, true);
        } else if (
          event.error?.details?.length > 0 &&
          event.error?.details[0]?.type === 'IneligibleProductForCartError' &&
          [
            'uniqueProductInCart',
            'uniqueTypeInCart',
            'uniqueProductInAdd',
            'allowedQuantityToAddExceeded',
            'allowedQuantityToUpdateExceeded',
            'uniqueTypeInAdd',
            'uniqueTypeInCartAndQuantityExceeded',
            'allowedQuantityToAddExceededAndUniqueType'
          ].includes(event.error?.details[0]?.message)
        ) {
          this.addedWithInvalidProductType = true;
          this.hideContinueShoppingBtn = [
            'allowedQuantityToAddExceededAndUniqueType',
            'uniqueTypeInCartAndQuantityExceeded'
          ].includes(event.error?.details[0]?.message);

          this.invalidProductTypeKey = event.error?.details[0]?.message;
          this.isOpened = true;
        } else {
          this.globalError = true;
          this.isOpened = true;
        }
        this.cdRef.markForCheck();
        this.loaderService.hideGlobalLoader();
      });

    this.eventService
      .get(CartAddEntrySuccessEvent)
      .pipe(
        switchMap((event) => {
          const normalizedProduct = this.converter.convert(event.entry.product, PRODUCT_NORMALIZER);
          return combineLatest([
            of(event),
            this.productMapper.productToProductCustomer(normalizedProduct)
          ]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(([event, productCustomer]) => {
        const normalizedProduct = this.converter.convert(event.entry.product, PRODUCT_NORMALIZER);
        const totalPriceWithDiscount = event['addedPriceWithDiscount']['formattedValue'];
        const totalPriceWithDiscountValue = event['addedPriceWithDiscount']['value'];
        const totalPriceWithoutDiscount = event['addedPriceWithoutDiscount']['formattedValue'];
        const totalPriceWithoutDiscountValue = event['addedPriceWithoutDiscount']['value'];

        const isNewPriceManagementActivated = this.baseStore.activateNewPriceManagement;

        const unitPrice = normalizedProduct.discountedPrice
          ? normalizedProduct.discountedPrice.formattedValue
          : normalizedProduct.price.formattedValue;
        this.isBundle = false;
        this.product = {
          ...productCustomer,
          familyAnneeN: event.entry.product?.familyAnneeN,
          family2: event.entry.product?.family2,
          quantity: event.quantityAdded,
          totalPrice: totalPriceWithoutDiscount,
          discountedPrice:
            !!totalPriceWithDiscountValue && totalPriceWithDiscountValue !== totalPriceWithoutDiscountValue
              ? totalPriceWithDiscount
              : undefined,
          price: unitPrice,
          recommendedPrice: isNewPriceManagementActivated ? normalizedProduct?.recommendedPrice?.formattedValue : undefined,
          isDiscountWithRecommendedPrice: !isNewPriceManagementActivated ? !normalizedProduct.strikethroughPrice : false,
          isTaxesIncluded: this.baseStore.displayTaxesIncluded,
          stock: {
            ...productCustomer?.stock,
            lowStock: undefined
          }
        };

        if (event['statusCode'] === 'success') {
          this.addedWithInvalidProductType = false;
          this.invalidProductTypeKey = '';
          this.hideContinueShoppingBtn = false;
        }
        this.quantityExceededError =
          (event['statusCode'] === 'maxOrderQuantityExceeded' || event['statusCode'] === StockStatusEnum.LOW_STOCK) &&
          event.quantityAdded === 0;
        this.addedWithQuantityExceededError =
          (event['statusCode'] === 'maxOrderQuantityExceeded' || event['statusCode'] === StockStatusEnum.LOW_STOCK) &&
          event.quantityAdded > 0;
        this.globalError =
          event['statusCode'] !== 'success' && !this.addedWithQuantityExceededError && !this.quantityExceededError;
        this.isOpened = true;

        if (!this.globalError && !this.quantityExceededError) {
          this.cartService.cartProductAddEntrySuccessEvent(event.quantityAdded, normalizedProduct);
        }

        if (this.isExtendedWarrantyEnabled && !this.product.isAccessory && !this.quantityExceededError) {
          this.setExtendedWarrantyData(this.product, event.entry.entryNumber, normalizedProduct, this.baseStore.currencies);
        }

        this.cdRef.markForCheck();
        this.loaderService.hideGlobalLoader();
      });

    this.cartService.addBundleToCart$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((event) => {
          if (!event?.bundle) {
            return of({ event, bundleCard: null });
          }
          return this.bundleMapper.bundleToIProductCustomer(event.bundle).pipe(
            map((bundleCard) => (
              { event, bundleCard }))
          );
        })
      )
      .subscribe(({ event, bundleCard }) => {
        this.extendedWarrantyData = [];
        this.isBundle = !!event?.bundle;
        const hasDiscountedPrice =
          !!event?.bundle?.price?.value && event?.bundle?.discountedPrice?.value
            ? event?.bundle?.discountedPrice?.value !== event?.bundle?.price?.value
            : event?.bundle?.discountedPrice?.formattedValue !== event?.bundle.price?.formattedValue;
        if (bundleCard?.stock?.lowStock) {
          bundleCard.stock.lowStock = undefined;
        }

        this.product = {
          ...(bundleCard || {}),
          quantity: event?.quantityAdded !== undefined ? event?.quantityAdded : event.quantity,
          totalPrice: event?.bundle?.price?.formattedValue,
          price:
            !!event?.bundle?.discountedPrice && hasDiscountedPrice
              ? event?.bundle?.discountedPrice?.formattedValue
              : undefined,
          isTaxesIncluded: this.baseStore.displayTaxesIncluded
        };

        this.quantityExceededError = event['statusCode'] === 'maxOrderQuantityExceeded' || event.quantityAdded === 0;
        this.addedWithQuantityExceededError = !!event.quantityAdded && !this.quantityExceededError;
        this.globalError =
          event['statusCode'] !== 'success' && !this.quantityExceededError && !this.addedWithQuantityExceededError;
        this.isOpened = true;

        if (!this.globalError && !this.quantityExceededError) {
          this.cartService.cartBundleAddEntrySuccessEvent(event.quantity, event.bundle);
        }
        this.cdRef.markForCheck();
        this.loaderService.hideGlobalLoader();
      });

    this.cartService.addForfaitToCart$
      .pipe(
        switchMap((event) => {
          const productCustomer$: Observable<IProductCustomer> = this.productMapper.productToProductCustomer(
            event.forfait
          );
          return combineLatest([of(event), productCustomer$]);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(([event, productCustomer]) => {
        const unitPrice = event.forfait?.discountedPrice
          ? event.forfait?.discountedPrice.formattedValue
          : event.forfait?.price.formattedValue;
        this.isBundle = false;
        this.product = {
          ...productCustomer,
          quantity: 1,
          totalPrice: unitPrice,
          isTaxesIncluded: this.baseStore.displayTaxesIncluded
        };
        this.globalError = event['statusCode'] !== 'success';

        if (!this.globalError) {
          const productEvent = this.trackingService.productToEventProduct(
            event.forfait,
            undefined,
            event.quantity,
            sessionStorage.getItem('addToCartContext')
          );
          sessionStorage.removeItem('addToCartContext');
          this.trackingService.sendEvent('clickAddToCart', productEvent);
          this.goToCart();
        } else {
          this.isOpened = true;
        }
        this.cdRef.markForCheck();
        this.loaderService.hideGlobalLoader();
      });
  }

  modalClosed(event: boolean): void {
    if (!event && this.globalError) {
      this.activeCartService
        .getActiveCartId()
        .pipe(take(1))
        .subscribe((cartId) => {
          this.multiCartService.reloadCart(cartId);
        });
    }
  }

  goToCart(): void {
    this.isOpened = false;
    this.router.navigate(['/cart']);
  }

  closeModal(): void {
    this.isOpened = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openRecommendedPriceModal() {
    this.isOpenedRecommendedModal = true;
  }

  onClickWarranty(warranty: IWarranty) {
    if (warranty.state) {
      this.trackingService.sendEvent('clickWarrantySelection', {
        positionLabel: 'Popin AddToCart',
        warrantyType: 'Duration ' + warranty.list[0]?.split(' ')[0]
      });
    }
    const type = warranty.state === true ? 'ADD' : 'DELETE';
    this.extendedWarrantyService.updateExtendedWarranty(warranty, type)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.activeCartService.reloadActiveCart();
      });
  }

  setExtendedWarrantyData(product, entryNumber, normalizedProduct, currencies) {
    const _product = {
      ...product,
      discountedPrice: normalizedProduct.discountedPrice,
      price: normalizedProduct.price
    };
    const extendedWarrantyOffersData$ = this.extendedWarrantyService.getAndSetExtendedWarrantyOffersData(
      _product,
      entryNumber,
      currencies);
    const cartEntries$ = this.cartService.getCartEntries().pipe(take(1));
    this.extendedWarrantyData = [];
    combineLatest([extendedWarrantyOffersData$, cartEntries$])
      .subscribe(([extendedWarrantyOffersData, cartEntries]) => {
        this.extendedWarrantyData = extendedWarrantyOffersData;
        cartEntries.forEach((cartEntry) => {
          if (cartEntry.product.code === product.cmmf) {
            this.extendedWarrantyData.forEach((extendedWarranty) => {
              extendedWarranty.state = extendedWarranty.id === cartEntry?.extendedWarrantyOffer?.offerId;
            });
            this.cdRef.markForCheck();
          }
        });
        this.cdRef.markForCheck();
      });
  }
}
